import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ExpiryCountdown } from '../components/temporary-numbers/ExpiryCountdown';
import { ReceivedCodesTable } from '../components/temporary-numbers/ReceivedCodesTable';
import { SmsChecker } from '../components/temporary-numbers/SmsChecker';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Loader } from '../components/ui/Loader';
import { useSse } from '../contexts/SseContext';
import { useLanguage } from '../hooks/useLanguage';
import {
  useGetTemporaryNumberQuery,
  useGetResendCapabilityQuery,
  useResendMutation,
} from '../store/api/temporaryNumbersApi';
import { formatDate } from '../utils/dateFormatter';
import { parsePhoneNumber } from '../utils/numberFormatter';

export function TemporaryNumberDetailsPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { numberId } = useParams<{ numberId: string }>();

  const { data, isLoading, error, refetch } = useGetTemporaryNumberQuery(numberId || '', {
    skip: !numberId,
  });
  const { getCodeForNumber } = useSse();
  const refetchedForCodeRef = useRef(false);

  const status = data?.data?.temporaryNumber?.status;
  const isActive = status === 'active';
  const isResendable = status === 'resendable';
  const isWaiting = status === 'waiting';
  const isTextVerifiedTemp = data?.data?.temporaryNumber?.serviceProvider === 'textverified';
  const isCodeReceived = data?.data?.temporaryNumber?.isCodeReceived === true;
  const { data: resendCapability } = useGetResendCapabilityQuery(numberId || '', {
    skip: !numberId || (!isActive && !isResendable && !(isTextVerifiedTemp && isCodeReceived)),
  });

  const [requestResend, { isLoading: isResending }] = useResendMutation();
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);

  const number = data?.data?.temporaryNumber;

  // When waiting and SSE delivers a code for this number → refetch so UI updates (e.g. status → resendable)
  useEffect(() => {
    if (!number || status !== 'waiting') {
      refetchedForCodeRef.current = false;
      return;
    }
    const sseCode = getCodeForNumber(number.number);
    if (sseCode && !refetchedForCodeRef.current) {
      refetchedForCodeRef.current = true;
      refetch();
    }
  }, [number, status, getCodeForNumber, refetch, data]);

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data || !number) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-4xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('errors.notFound')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {t('temporaryNumbers.details.notFound')}
              </p>
              <Link
                to="/temporary-numbers/list"
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('temporaryNumbers.list.title')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (s: string) => {
    switch (s) {
      case 'active':
        return 'bg-green-500/20 text-green-500';
      case 'resendable':
        return 'bg-emerald-500/20 text-emerald-400';
      case 'waiting':
        return 'bg-amber-500/20 text-amber-400';
      case 'expired':
        return 'bg-neutral-dark text-neutral-medium';
      case 'refunded':
        return 'bg-blue-500/20 text-blue-500';
      case 'cancelled':
        return 'bg-red-500/20 text-red-500';
      default:
        return 'bg-neutral-dark text-neutral-medium';
    }
  };

  const handleCopyNumber = async () => {
    try {
      await navigator.clipboard.writeText(number.number);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('common.copyError') || t('errors.network'));
    }
  };

  const resendData = resendCapability?.data;
  const canResendFromApi =
    resendData?.resendSupported === true && resendData?.available === true;
  const canResend = (isResendable || isActive || (isTextVerifiedTemp && isCodeReceived)) && canResendFromApi;

  const handleResend = async () => {
    if (!numberId) return;
    try {
      await requestResend(numberId).unwrap();
      toast.success(t('temporaryNumbers.resend.success'));
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'data' in err && err.data && typeof err.data === 'object' && 'message' in err.data
          ? String((err.data as { message: unknown }).message)
          : t('errors.network');
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between animate-slide-up">
            <div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
                {t('temporaryNumbers.details.title')}
              </h1>
            </div>
            <Link
              to="/temporary-numbers/list"
              className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
            >
              {t('common.back')}
            </Link>
          </div>

          {/* Details Card */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mb-6 animate-scale-in">
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between pb-4 border-b border-neutral-dark">
                <span className="text-neutral-medium font-body">
                  {t('temporaryNumbers.details.status')}
                </span>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm font-body ${getStatusColor(number.status)}`}
                >
                  {t(`temporaryNumbers.status.${number.status}`)}
                </span>
              </div>

              {/* Phone Number */}
              <div className="pb-4 border-b border-neutral-dark">
                <p className="text-sm text-neutral-medium font-body mb-2">
                  {t('temporaryNumbers.details.number')}
                </p>
                <div className="flex items-center gap-3">
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {(() => {
                      const { countryCode, localNumber } = parsePhoneNumber(number.number);
                      return `${countryCode} ${localNumber}`;
                    })()}
                  </p>
                  <button
                    onClick={handleCopyNumber}
                    className="p-2 bg-brand-accent/20 hover:bg-brand-accent/30 text-brand-accent rounded-lg transition-all duration-300 hover:scale-105"
                    title={t('common.copy')}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Service Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('temporaryNumbers.details.service')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {number.serviceName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('temporaryNumbers.details.country')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {number.countryName}
                  </p>
                </div>
                {(number.stateName || number.areaCode) && (
                  <div>
                    <p className="text-sm text-neutral-medium font-body mb-1">
                      {t('temporaryNumbers.details.state')}
                    </p>
                    <p className="text-lg font-heading font-semibold text-neutral-light">
                      {number.stateName ? `${number.stateName} (${number.areaCode})` : number.areaCode}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('temporaryNumbers.details.price')}
                  </p>
                  <p className="text-lg font-heading font-bold text-brand-accent">
                    {number.price.toFixed(2)} USD
                  </p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-neutral-dark">
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('temporaryNumbers.details.purchasedAt')}
                  </p>
                  <p className="text-base font-body text-neutral-light">
                    {formatDate(number.purchasedAt, 'PP h:mm a', currentLanguage)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('temporaryNumbers.details.expiresAt')}
                  </p>
                  <p className="text-base font-body text-neutral-light">
                    {isResendable && number.resendExpiresAt
                      ? formatDate(number.resendExpiresAt, 'PP h:mm a', currentLanguage)
                      : formatDate(number.expiresAt, 'PP h:mm a', currentLanguage)}
                  </p>
                </div>
              </div>

              {/* Resend price (when available) */}
              {(number.resendPrice != null && number.resendPrice > 0) && (
                <div className="pt-4 border-t border-neutral-dark">
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('temporaryNumbers.details.resendPrice')}
                  </p>
                  <p className="text-base font-body font-semibold text-brand-accent">
                    {Number(number.resendPrice).toFixed(2)} USD
                  </p>
                </div>
              )}

              {/* Additional Info (cashback only; duration removed) */}
              {number.cashbackAmount != null && number.cashbackAmount > 0 && (
                <div className="pt-4 border-t border-neutral-dark">
                  <span className="text-neutral-medium font-body text-sm">
                    {t('temporaryNumbers.details.cashback')}:
                  </span>
                  <span className="text-green-500 font-body text-sm ml-2">
                    {number.cashbackAmount.toFixed(2)} USD
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Resend (reuse) – when resendable (resendExpiresAt valid) or active with API support */}
          {canResend && (
            <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mb-6 animate-scale-in">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-heading font-semibold text-neutral-light">
                    {t('temporaryNumbers.resend.title')}
                  </h2>
                  {resendData?.resendPrice != null && (
                    <p className="text-sm text-neutral-medium font-body mt-1">
                      {t('temporaryNumbers.resend.costLabel')}:{' '}
                      <span className="text-brand-accent font-semibold">
                        {Number(resendData.resendPrice).toFixed(2)} USD
                      </span>
                      {resendData.resendsAvailable != null && resendData.resendsAvailable > 0 && (
                        <span className="text-neutral-light ml-2">
                          ({t('temporaryNumbers.resend.resendsAvailable', { count: resendData.resendsAvailable })})
                        </span>
                      )}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setResendConfirmOpen(true)}
                  disabled={isResending}
                  className="px-5 py-2.5 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isResending ? t('temporaryNumbers.resend.requesting') : t('temporaryNumbers.resend.button')}
                </button>
              </div>
            </div>
          )}

          {/* Waiting: countdown + SSE */}
          {isWaiting && (
            <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mb-6 animate-scale-in">
              <h2 className="text-xl font-heading font-semibold text-neutral-light mb-2">
                {t('temporaryNumbers.sms.title')}
              </h2>
              <p className="text-sm text-neutral-medium font-body mb-4">
                <ExpiryCountdown expiresAt={number.expiresAt} onExpired={refetch} />
              </p>
              <SmsChecker
                temporaryNumberId={number._id}
                phoneNumber={number.number}
                isCodeReceived={number.isCodeReceived}
                receivedCodes={number.receivedCodes ?? []}
              />
            </div>
          )}

          {/* Received codes: show whenever we have codes (active, resendable, expired, refunded, etc.) – not in waiting */}
          {!isWaiting && (number.receivedCodes?.length ?? 0) > 0 && (
            <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
              <ReceivedCodesTable
                receivedCodes={number.receivedCodes ?? []}
                pageSize={5}
              />
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={resendConfirmOpen}
        onClose={() => setResendConfirmOpen(false)}
        onConfirm={() => {
          void handleResend();
          setResendConfirmOpen(false);
        }}
        title={t('temporaryNumbers.resend.title')}
        message={t('temporaryNumbers.resend.confirmMessage')}
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
        type="warning"
        isLoading={isResending}
      />
    </div>
  );
}

